/**
 * Query the browser's geolocation permission without triggering a prompt.
 * Used to distinguish "will show native popup" vs "already blocked in
 * site settings" so we can show enable-in-browser guidance instead of
 * silently failing getCurrentPosition.
 */

export type BrowserGeoPermission = 'granted' | 'denied' | 'prompt' | 'unknown';

export const BROWSER_GEO_BLOCKED_MESSAGE =
  'Location is blocked for this site. Click the lock icon in your browser address bar, set Location to Allow, then try again.';

export async function queryBrowserGeoPermission(): Promise<BrowserGeoPermission> {
  if (typeof navigator === 'undefined' || !navigator.geolocation) {
    return 'unknown';
  }

  // Secure context required — http:// on a LAN host will never show a popup.
  if (typeof window !== 'undefined' && !window.isSecureContext) {
    return 'denied';
  }

  try {
    if (!navigator.permissions?.query) return 'unknown';
    // `geolocation` is widely supported; cast keeps TS happy across lib.dom versions.
    const result = await navigator.permissions.query({
      name: 'geolocation' as PermissionName,
    });
    if (result.state === 'granted' || result.state === 'denied' || result.state === 'prompt') {
      return result.state;
    }
  } catch {
    // Safari / older browsers may reject the query — fall through to unknown
    // and let getCurrentPosition decide.
  }

  return 'unknown';
}
